import React, { useEffect, useState, useCallback } from "react";
import { Building2, Search, Plus, X, Check } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/**
 * Canonical Organisation picker — search an existing org (fuzzy) or create a new
 * lightweight one. On select/create, calls onSelect(org) with the full org doc so
 * the caller can inherit sector/size/geography (no re-entry). Callers may pass a
 * seedName to prefill the create field. Clearing calls onSelect(null).
 */
export function OrgPicker({ selected, onSelect, seedName = "", label = "Organisation" }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [meta, setMeta] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState(seedName || "");
  const [newSector, setNewSector] = useState("");
  const [dupes, setDupes] = useState([]);

  useEffect(() => {
    api.get("/admin/organisations/meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  // Duplicate guard — warn when the typed name closely matches an existing org.
  useEffect(() => {
    if (!creating || !newName.trim()) { setDupes([]); return; }
    const t = setTimeout(() => {
      api.get("/admin/organisations/match", { params: { name: newName.trim() } })
        .then(({ data }) => setDupes(data.matches || []))
        .catch(() => setDupes([]));
    }, 350);
    return () => clearTimeout(t);
  }, [newName, creating]);

  const search = useCallback((term) => {
    api.get("/admin/organisations", { params: term ? { q: term } : {} })
      .then(({ data }) => setRows(data.organisations || []))
      .catch(() => setRows([]));
  }, []);

  useEffect(() => {
    if (open) search(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pick = async (row) => {
    try {
      const { data } = await api.get(`/admin/organisations/${row.id}`);
      onSelect(data);
      setOpen(false);
      toast.success(`Linked to ${data.legal_name}`);
    } catch {
      toast.error("Could not load organisation");
    }
  };

  const create = async () => {
    if (!newName.trim()) return toast.error("Organisation name is required");
    try {
      const { data } = await api.post("/admin/organisations", {
        legal_name: newName.trim(),
        sector: newSector || null,
      });
      onSelect(data);
      setCreating(false);
      setOpen(false);
      toast.success("Organisation created");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
  };

  if (selected) {
    return (
      <div className="ek-surface-2 border ek-line rounded-lg p-3 flex items-center justify-between gap-3" data-testid="org-picker-selected">
        <div className="flex items-center gap-2 min-w-0">
          <Building2 size={16} className="text-[#7737F6] shrink-0" />
          <div className="min-w-0">
            <div className="text-[13px] font-medium ek-text truncate">{selected.legal_name}</div>
            <div className="text-[11px] ek-text-muted truncate">
              {(selected.sector_label || selected.sector || "sector —")}
              {selected.archetype_name ? ` · ${selected.archetype_name}` : ""}
              {typeof selected.completeness_pct === "number" ? ` · ${selected.completeness_pct}% complete` : ""}
            </div>
          </div>
        </div>
        <button type="button" onClick={() => onSelect(null)} data-testid="org-picker-clear"
          className="text-[11px] ek-text-muted hover:text-rose-500 flex items-center gap-1 shrink-0">
          <X size={13} /> Unlink
        </button>
      </div>
    );
  }

  return (
    <div className="border ek-line rounded-lg p-3" data-testid="org-picker">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-medium tracking-[0.16em] ek-text-muted uppercase">{label} (optional)</span>
        {!creating && (
          <button type="button" onClick={() => { setCreating(true); setOpen(false); }} data-testid="org-picker-new-btn"
            className="text-[11px] text-[#7737F6] flex items-center gap-1"><Plus size={13} /> New</button>
        )}
      </div>

      {creating ? (
        <div className="space-y-2">
          <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
            className="w-full border ek-line px-3 py-2 text-[13px] ek-surface" placeholder="Legal / company name"
            data-testid="org-picker-new-name" />
          {dupes.length > 0 && (
            <div className="border border-amber-300 bg-amber-50 rounded-md p-2.5" data-testid="org-picker-dupe-warning">
              <div className="text-[11px] font-medium text-amber-800 mb-1">
                Possible duplicate — link an existing organisation instead of creating a new one?
              </div>
              <div className="space-y-1">
                {dupes.map((d) => (
                  <button type="button" key={d.id} onClick={() => pick(d)} data-testid={`org-picker-dupe-${d.id}`}
                    className="w-full text-left text-[12px] text-amber-900 hover:underline flex items-center justify-between gap-2">
                    <span className="truncate">{d.legal_name}</span>
                    <span className="text-[11px] text-amber-700 shrink-0">{d.archetype_name || d.sector || "—"} · Link</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <select value={newSector} onChange={(e) => setNewSector(e.target.value)}
            className="w-full border ek-line px-3 py-2 text-[13px] ek-surface" data-testid="org-picker-new-sector">
            <option value="">Sector (optional)…</option>
            {(meta?.sectors || []).map((s) => <option key={s.slug} value={s.slug}>{s.label}</option>)}
          </select>
          <div className="flex gap-2">
            <button type="button" onClick={create} data-testid="org-picker-create-confirm"
              className="ek-btn ek-btn-primary text-[12px] flex items-center gap-1"><Check size={13} /> Create & link</button>
            <button type="button" onClick={() => setCreating(false)} className="ek-btn ek-btn-outline text-[12px]">Cancel</button>
          </div>
        </div>
      ) : (
        <>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 ek-text-muted" />
            <input value={q} onFocus={() => setOpen(true)}
              onChange={(e) => { setQ(e.target.value); setOpen(true); search(e.target.value); }}
              className="w-full border ek-line pl-9 pr-3 py-2 text-[13px] ek-surface"
              placeholder="Search existing organisations…" data-testid="org-picker-search" />
          </div>
          {open && (
            <div className="mt-2 max-h-56 overflow-auto border ek-line rounded-md divide-y divide-[color:var(--ek-line,#e5e7eb)]" data-testid="org-picker-results">
              {rows.length === 0 ? (
                <div className="px-3 py-3 text-[12px] ek-text-muted">No organisations found. Use “New”.</div>
              ) : rows.map((o) => (
                <button type="button" key={o.id} onClick={() => pick(o)} data-testid={`org-picker-row-${o.id}`}
                  className="w-full text-left px-3 py-2 hover:bg-[#7737F6]/5 flex items-center justify-between gap-2">
                  <span className="text-[13px] ek-text truncate">{o.legal_name}</span>
                  <span className="text-[11px] ek-text-muted shrink-0">{o.archetype_name || o.sector || "—"}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default OrgPicker;
